/**
 * @fileoverview UpcomingScheduleSection — Dashboard "Upcoming Schedule" strip.
 *
 * Shows 3–5 admin-planned meetings from the Upcoming Schedule feature.
 * Deliberately compact (a slim row of small cards) so it never dominates
 * the dashboard — sits directly below the live/upcoming Showdown card and
 * the Invite Friends card.
 *
 * Labels are honest about what's actually playable:
 *   - "Planned"      — admin-scheduled meeting, no real Showdown yet
 *   - "Opening Soon" / "Entries Open" / "Live" / "Completed"
 *                     — a real Showdown now exists; label comes from the
 *                       linked slate's own getShowdownDisplayState.
 */

import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { subscribePublicUpcomingSchedule } from '../../services/upcomingMeetingsService';
import { getUpcomingMeetingDisplayState, formatMeetingDateShort } from '../../utils/upcomingMeetingStatus';
import { getShowdownDisplayState } from '../../utils/showdownStatus';
import './UpcomingScheduleSection.css';

const MAX_ITEMS = 5;

export default function UpcomingScheduleSection({ slates = [] }) {
  const navigate = useNavigate();
  const [meetings, setMeetings] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    return subscribePublicUpcomingSchedule(
      (rows) => { setMeetings(rows); setLoaded(true); },
      (err) => { console.warn('[UpcomingScheduleSection] subscribe failed:', err.message); setLoaded(true); }
    );
  }, []);

  const slateById = useMemo(() => {
    const map = new Map();
    slates.forEach((s) => map.set(s.id, s));
    return map;
  }, [slates]);

  const items = useMemo(() => {
    const todayIso = new Date().toISOString().split('T')[0];
    return meetings
      .filter((m) => !m.meetingDate || m.meetingDate >= todayIso)
      .slice(0, MAX_ITEMS)
      .map((m) => {
        const slate = m.convertedSlateId ? slateById.get(m.convertedSlateId) : null;
        const slateState = slate ? getShowdownDisplayState(slate) : null;
        return { meeting: m, display: getUpcomingMeetingDisplayState(m, slateState) };
      });
  }, [meetings, slateById]);

  if (!loaded) return null;

  return (
    <div className="dp-schedule">
      <div className="dp-schedule__head">
        <h3 className="dp-schedule__title">Upcoming Schedule</h3>
        <p className="dp-schedule__subtitle">Planned Showdowns coming soon.</p>
      </div>

      {items.length === 0 ? (
        <p className="dp-schedule__empty">No planned showdowns yet. Check back soon.</p>
      ) : (
      <div className="dp-schedule__row">
        {items.map(({ meeting, display }) => (
          <div
            key={meeting.meetingKey}
            className={`dp-schedule-card dp-schedule-card--${display.tone}${display.isClickable ? ' dp-schedule-card--clickable' : ''}`}
            onClick={() => { if (display.isClickable && display.route) navigate(display.route); }}
          >
            <span className={`dp-schedule-card__badge dp-schedule-card__badge--${display.tone}`}>
              {display.label}
            </span>
            <span className="dp-schedule-card__track">{meeting.trackName}</span>
            <span className="dp-schedule-card__meta">
              {formatMeetingDateShort(meeting.meetingDate)} · {meeting.state}
            </span>
            {display.isClickable && (
              <span className="dp-schedule-card__cta">
                {display.label === 'Live' ? 'Play Now →' : 'View Showdown →'}
              </span>
            )}
          </div>
        ))}
      </div>
      )}
    </div>
  );
}
